import React from 'react';
import c from 'classnames';
import Immutable from 'immutable';
import { type TFunction, useTranslation } from 'react-i18next';

import { Taxonomy } from '@uncinc/react-drupal-core';
import { Icon } from '@uncinc/uncinc-react-kitchen-sink';
import type { Langcodes } from '@uncinc/react-drupal-core/types';

import Block from '../Block';
import Button from '../Button';
import eventDateInfo from '../../helpers/eventDateInfo';
import { NodeEvent } from '../../types/uncinc_profile/nodes';
import { DEFAULT_LANGUAGE } from '../../config/constants';
import { dateFormatter, timeFormatter } from '../../helpers/dateTimeFormatter';

import './style.scss';

interface BlockEventInfoProps extends React.ComponentPropsWithoutRef<'div'> {
  event: NodeEvent;
}

const RegistrationButton = ({ event, t, lang }: { event: NodeEvent, t: TFunction<'translation', undefined>, lang: Langcodes }) => {
  const link = event.field_link[0]?.alias || event.field_link[0]?.uri;
  const linkTitle = event.field_link[0]?.title ?? '';
  const registrationOpenDate = event.field_registration_open_date[0]?.value;

  const eventDates = event.field_date;
  const eventStatus = event.field_event_status[0]?.entity.name[0]?.value;

  const {
    firstUpcomingDate,
    isOpenForRegistration,
    isPublic,
  } = eventDateInfo(eventDates, registrationOpenDate, eventStatus);

  if (isPublic && !link) {
    return <div />;
  }

  const isEventOver = !firstUpcomingDate;
  const isDisabled = !isOpenForRegistration || !isPublic || isEventOver;
  const registrationButtonText = linkTitle || t('components.block_event_info.registration');

  let helperText = t('components.block_event_info.registration_closed');

  if (registrationOpenDate && !isOpenForRegistration) {
    helperText = t('components.block_event_info.registration_not_yet_open', { date: dateFormatter(registrationOpenDate, lang) });
  } else if (isEventOver) {
    helperText = t('components.block_event_info.event_over');
  }

  return (
    <div className="BlockEventInfo__link">
      <Button
        href={link}
        type="primary"
        disabled={isDisabled}
      >
        {registrationButtonText}
      </Button>

      {isDisabled && (
        <p className="mt-3">
          {helperText}
        </p>
      )}
    </div>
  );
};

const BlockEventInfo: React.FC<BlockEventInfoProps> = ({ event, className, ...props }) => {
  const { t } = useTranslation();

  const classNames = c('BlockEventInfo', className);

  const lang = event.langcode[0]?.value || DEFAULT_LANGUAGE;
  const { dates } = eventDateInfo(event.field_date);

  return (
    <Block className={classNames} {...props}>
      <h2 className="asH3 mb-6">{t('components.block_event_info.title')}</h2>
      <div className="BlockEventInfo__group">
        <div className="BlockEventInfo__icon">
          <Icon name="calendar_today" />
        </div>
        <div className="BlockEventInfo__content">
          <p className="label">{t('components.block_event_info.date_time')}</p>
          {dates.map((date) => (
            <time className="body--small mb-1">
              {dateFormatter(date.start.date, lang)} &bull; {timeFormatter(date.start.date, lang)}
              {date.end && <> &#45; {timeFormatter(date.end.date, lang)}</>}
            </time>
          ))}
        </div>
      </div>
      {event.field_location[0] && (
        <div className="BlockEventInfo__group">
          <div className="BlockEventInfo__icon">
            <Icon name="location_on" />
          </div>
          <div className="BlockEventInfo__content">
            <p className="label">{t('components.block_event_info.location')}</p>
            <Taxonomy
              viewMode="event"
              entity={Immutable.fromJS(event.field_location[0].entity)}
            />
          </div>
        </div>
      )}
      <RegistrationButton event={event} t={t} lang={lang} />
    </Block>
  );
};

export default BlockEventInfo;
